import { useEffect, useRef, useState } from 'react';
import { SendHorizonal, BookOpen, Loader2, Mic, MicOff, Volume2 } from 'lucide-react';
import type { AuditEvent, Incident } from '@duty/shared';
import { Pulse, type PulseState } from './Pulse';
import { askDuty, type AskResult } from '@/incident/doctrine';
import { fetchBriefing, buildBriefingLocal, type BriefingResult } from '@/incident/briefing';
import { useVoice } from '@/hooks/useVoice';
import type { Insight } from '@duty/shared';

const AUDIT_LABEL: Partial<Record<AuditEvent['type'], string>> = {
  'insight.shown': 'raised',
  'insight.acknowledged': 'acknowledged',
  'insight.dismissed': 'dismissed',
  'insight.auto-resolved': 'auto-resolved',
};

/**
 * The assistant rail — Duty's presence (the Pulse), a grounded situational
 * Briefing (AI-rephrased with deterministic fallback), Ask Duty doctrine Q&A
 * with optional mic input, and a live audit ticker.
 */
export function AssistantRail({
  pulseState = 'idle',
  audit = [],
  incident,
  insights = [],
}: {
  pulseState?: PulseState;
  audit?: AuditEvent[];
  incident: Incident | null;
  insights?: readonly Insight[];
}) {
  const recent = [...audit].slice(-5).reverse();
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<AskResult | null>(null);
  const voice = useVoice();

  // --- Briefing (AI-rephrased, deterministic fallback) -------------------------
  const [briefing, setBriefing] = useState<BriefingResult | null>(null);
  const briefingCtrl = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!incident) {
      setBriefing(null);
      return;
    }
    // Local briefing immediately while we wait for the API.
    setBriefing(buildBriefingLocal(incident, insights));
    briefingCtrl.current?.abort();
    const ctrl = new AbortController();
    briefingCtrl.current = ctrl;
    fetchBriefing(incident, insights, ctrl.signal)
      .then((r) => { if (!ctrl.signal.aborted) setBriefing(r); })
      .catch(() => { /* fallback already set */ });
    return () => ctrl.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incident?.id]);

  // --- Effective Pulse state (listening overrides parent) ----------------------
  const effectivePulseState: PulseState = voice.listening
    ? 'listening'
    : busy
    ? 'analysing'
    : pulseState;

  // --- Ask Duty ----------------------------------------------------------------
  async function onAsk(e: React.FormEvent) {
    e.preventDefault();
    const q = query.trim();
    if (!q || busy) return;
    setBusy(true);
    try {
      const r = await askDuty(q, incident);
      setResult(r);
      if (r.answer) voice.speak(r.answer);
    } finally {
      setBusy(false);
    }
  }

  function toggleMic() {
    if (voice.listening) {
      voice.stopListening();
    } else {
      voice.startListening((transcript) => {
        setQuery(transcript);
      });
    }
  }

  return (
    <aside className="flex h-full flex-col gap-3" aria-label="Duty assistant">
      <section className="panel flex items-center gap-3 p-3.5">
        <Pulse state={effectivePulseState} size={56} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">Duty</p>
          <p className="text-xs text-muted">
            advisory assistant ·{' '}
            {voice.listening ? 'listening' : busy ? 'analysing' : pulseState}
          </p>
        </div>
        {briefing && (
          <button
            onClick={() => voice.speak(briefing.text)}
            disabled={!voice.voiceAvailable || voice.speaking}
            title={voice.voiceAvailable ? 'Read briefing aloud' : 'Enable voice in settings'}
            aria-label="Read briefing aloud"
            className="shrink-0 rounded-md p-1.5 text-muted transition-colors hover:text-accent disabled:opacity-30"
          >
            <Volume2 size={15} aria-hidden />
          </button>
        )}
      </section>

      <section className="panel thin-scrollbar flex-1 overflow-auto p-3.5">
        {result ? (
          <AskAnswer result={result} onClear={() => setResult(null)} />
        ) : (
          <>
            <h2 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
              Briefing
              {briefing?.aiUsed && (
                <span className="ml-1.5 font-normal normal-case text-accent/70">AI</span>
              )}
            </h2>
            <p className="text-sm leading-relaxed text-text/90">
              {briefing?.text ?? 'Loading briefing…'}
            </p>
            <p className="mt-3 text-[11px] italic text-muted">
              Advisory only. Grounded in entered context; sources shown per claim.
            </p>
          </>
        )}

        {recent.length > 0 && !result && (
          <div className="mt-4 border-t border-border pt-3">
            <h3 className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted">
              Audit
            </h3>
            <ul className="space-y-1">
              {recent.map((e) => (
                <li key={e.id} className="flex items-baseline gap-2 text-[11px] text-muted">
                  <span className="font-mono text-[10px]">
                    {new Date(e.at).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  <span>
                    {AUDIT_LABEL[e.type] ?? e.type}
                    {typeof e.payload?.title === 'string' && (
                      <span className="text-text/80"> — {e.payload.title as string}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <form className="panel flex items-center gap-2 p-2" onSubmit={onAsk} aria-label="Ask Duty">
        {voice.recognitionSupported && (
          <button
            type="button"
            onClick={toggleMic}
            aria-label={voice.listening ? 'Stop listening' : 'Start voice input'}
            aria-pressed={voice.listening}
            className={`rounded-md p-2 transition-colors ${
              voice.listening
                ? 'text-accent'
                : 'text-muted hover:text-accent'
            }`}
          >
            {voice.listening ? <MicOff size={16} aria-hidden /> : <Mic size={16} aria-hidden />}
          </button>
        )}
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="min-w-0 flex-1 bg-transparent px-1 text-sm placeholder:text-muted focus:outline-none"
          placeholder={voice.listening ? 'Listening…' : 'Ask Duty about doctrine…'}
          aria-label="Ask Duty a question"
        />
        <button
          type="submit"
          aria-label="Send"
          disabled={busy}
          className="rounded-md p-2 text-muted hover:text-accent disabled:opacity-50"
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : <SendHorizonal size={16} />}
        </button>
      </form>
    </aside>
  );
}

function AskAnswer({ result, onClear }: { result: AskResult; onClear: () => void }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">
          Ask Duty {result.aiUsed ? '· AI' : '· deterministic'}
        </h2>
        <button onClick={onClear} className="text-[11px] text-accent">
          back to briefing
        </button>
      </div>
      <p className="whitespace-pre-wrap text-sm leading-relaxed text-text/90">{result.answer}</p>

      {result.citations.length > 0 && (
        <div className="mt-3 border-t border-border pt-2">
          <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted">
            Sources ({result.eligibleDocs} eligible)
          </h3>
          <ul className="space-y-1">
            {result.citations.map((c, i) => (
              <li key={`${c.docId}-${i}`} className="flex items-start gap-1.5 text-[11px] text-muted">
                <BookOpen size={11} className="mt-0.5 shrink-0 text-accent" />
                <span>{c.docTitle}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="mt-3 text-[11px] italic text-muted">
        Sources selected deterministically (applicability-filtered). Advisory only.
      </p>
    </div>
  );
}
